import { z } from "zod";
import { toast } from "sonner";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { StateInput } from "@/app/Cities/Cities/Components/StateInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { GraphQLClientError } from "@/lib/GraphQLCodegen/GraphQLClientError";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { State } from "@/gql/graphql";
import {
  createSupplierCityMutation,
  CreatedSupplierCity,
} from "@/app/Cities/Cities/GraphQL/createSupplierCityMutation";

type FormValues = {
  name: string;
  state: State | null;
};

const validationSchema = z.object({
  name: z.string().min(1, { error: "Enter the city name" }),
  state: z.enum(State, { error: "Select a state" }),
});

function extractReason(error: unknown): string | undefined {
  if (error instanceof GraphQLClientError) {
    const extensions = error.response.errors?.[0]?.extensions as
      | { reason?: string }
      | undefined;

    return extensions?.reason;
  }

  return undefined;
}

export const SupplierCityCreateDialog: DialogComponent<
  { initialName?: string },
  CreatedSupplierCity
> = ({ initialName }) => {
  const { resolve, close } = useDialogState<CreatedSupplierCity>();

  const { mutateAsync } = useGqlMutation(createSupplierCityMutation, {
    onError: (error) => {
      toast.error("Couldn't add city", {
        description: extractReason(error) ?? (error as Error).message,
      });
    },
  });

  return (
    <BaseDialog
      title="Add a new city"
      subtitle="Use this only when the actual pickup town isn't in the list. The city is geocoded and rejected if it's within 10km of an existing one."
      size="lg"
    >
      <GenericForm<FormValues>
        validationSchema={toFormikValidationSchema(validationSchema)}
        initialValues={{ name: initialName ?? "", state: null }}
        onSubmit={async (values) => {
          try {
            const { createSupplierCity } = await mutateAsync({
              name: values.name,
              state: values.state as State,
            });

            toast.success("City added", {
              description: `${createSupplierCity.name} is now selectable.`,
            });

            resolve?.(createSupplierCity);

            return close();
          } catch {
            // Surfaced to the user via the mutation's onError toast.
          }
        }}
      >
        <TextInput
          name="name"
          label="City / town name"
          className="col-span-full"
        />

        <StateInput
          name="state"
          label="State / region"
          className="col-span-full"
        />

        <DialogSubmitButton text="Add city" />
      </GenericForm>
    </BaseDialog>
  );
};
